import React, { useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import { Popover } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { CampaignPipelineStage, CampaignStatusTrackingCreator } from "@/api/campaignStatusTracking";
import { getCountryFlag, getCountryFlagUrl, getCountryShort } from "@/shared/utils/formatters";

/** Figma `status_popover_searchable`: show search once the list exceeds 100 creators. */
const SEARCH_THRESHOLD = 100;

const PopoverGlobalStyle = createGlobalStyle`
	.campaign-status-creators-popover .ant-popover-inner {
		padding: 8px;
		border-radius: 12px;
		box-shadow: 0 8px 24px rgba(21, 89, 165, 0.14);
	}

	.campaign-status-creators-popover .ant-popover-inner-content {
		padding: 0;
	}
`;

const PopoverBody = styled.div`
	width: 238px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PopoverHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 2px 8px;
`;

const HeaderLabel = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 10px;
	font-weight: 600;
	color: #303030;
	white-space: nowrap;
`;

const HeaderSpacer = styled.span`
	flex: 1 1 auto;
	min-width: 8px;
	height: 1px;
`;

const HeaderCount = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 10px;
	font-weight: 600;
	color: #707070;
	white-space: nowrap;
`;

const SearchField = styled.label`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	box-sizing: border-box;
	height: 34px;
	padding: 8px 10px;
	border-radius: 8px;
	border: 1px solid #dde2fa;
	background: #fafbff;
	cursor: text;
`;

const SearchIcon = styled(SearchOutlined)`
	color: #9e9e9e;
	font-size: 12px;
	flex-shrink: 0;
`;

const SearchInput = styled.input`
	flex: 1 1 auto;
	min-width: 0;
	border: none;
	outline: none;
	background: transparent;
	font-family: "Poppins", sans-serif;
	font-size: 12px;
	font-weight: 400;
	line-height: 18px;
	color: #303030;

	&::placeholder {
		color: #9e9e9e;
	}
`;

const CreatorList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 8px;
	max-height: 256px;
	overflow-y: auto;
`;

const CreatorRow = styled.button`
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
	padding: 6px 8px;
	border: none;
	border-radius: 8px;
	background: #fff;
	cursor: pointer;
	text-align: left;

	&:hover {
		background: #f7f8ff;
	}
`;

const Avatar = styled.img`
	width: 24px;
	height: 24px;
	border-radius: 999px;
	object-fit: cover;
	background: #e7e5ff;
	flex-shrink: 0;
`;

const CreatorName = styled.span`
	flex: 1 1 auto;
	min-width: 0;
	font-family: "Poppins", sans-serif;
	font-size: 13px;
	font-weight: 400;
	color: #303030;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const MarketChip = styled.span`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	height: 22px;
	padding: 2px 6px;
	border-radius: 6px;
	background: #f7f7f7;
	flex-shrink: 0;
`;

const MarketFlag = styled.img`
	width: 14px;
	height: 14px;
	border-radius: 50%;
	object-fit: cover;
	display: block;
`;

const MarketFlagFallback = styled.span`
	width: 14px;
	height: 14px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 10px;
	line-height: 1;
`;

const MarketCode = styled.span`
	font-family: "Poppins", sans-serif;
	font-size: 10px;
	font-weight: 600;
	color: #4460ef;
	white-space: nowrap;
`;

const EmptyState = styled.span`
	padding: 8px;
	font-family: "Poppins", sans-serif;
	font-size: 13px;
	color: #767676;
`;

const DEFAULT_AVATAR = "/default_avatar.png";

const CreatorAvatar: React.FC<{ src: string | null; alt: string }> = ({ src, alt }) => {
	const [avatarSrc, setAvatarSrc] = useState(src?.trim() || DEFAULT_AVATAR);

	useEffect(() => {
		setAvatarSrc(src?.trim() || DEFAULT_AVATAR);
	}, [src]);

	return (
		<Avatar
			src={avatarSrc}
			alt={alt}
			onError={() => {
				if (avatarSrc !== DEFAULT_AVATAR) setAvatarSrc(DEFAULT_AVATAR);
			}}
		/>
	);
};

interface StageCreatorsPopoverProps {
	stage: CampaignPipelineStage;
	stageLabel: string;
	creators: CampaignStatusTrackingCreator[];
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onCreatorClick: (creator: CampaignStatusTrackingCreator) => void;
	children: React.ReactNode;
}

export const StageCreatorsPopover: React.FC<StageCreatorsPopoverProps> = ({
	stageLabel,
	creators,
	open,
	onOpenChange,
	onCreatorClick,
	children,
}) => {
	const [searchQuery, setSearchQuery] = useState("");
	const showSearch = creators.length > SEARCH_THRESHOLD;

	useEffect(() => {
		if (!open) setSearchQuery("");
	}, [open]);

	const filteredCreators = useMemo(() => {
		if (!showSearch) return creators;
		const query = searchQuery.trim().toLowerCase();
		if (!query) return creators;
		return creators.filter((creator) => {
			const name = (creator.displayName || "").toLowerCase();
			const handle = (creator.handle || "").toLowerCase();
			const market = (creator.marketName || "").toLowerCase();
			return name.includes(query) || handle.includes(query) || market.includes(query);
		});
	}, [creators, searchQuery, showSearch]);

	const content = (
		<PopoverBody>
			<PopoverHeader>
				<HeaderLabel>{stageLabel}</HeaderLabel>
				<HeaderSpacer />
				<HeaderCount>{creators.length}</HeaderCount>
			</PopoverHeader>
			{showSearch && (
				<SearchField
					onMouseDown={(event) => {
						// Keep popover open while focusing the search input.
						event.stopPropagation();
					}}
				>
					<SearchIcon aria-hidden />
					<SearchInput
						type="search"
						value={searchQuery}
						placeholder="Search creators"
						aria-label="Search creators"
						onChange={(event) => setSearchQuery(event.target.value)}
						onKeyDown={(event) => event.stopPropagation()}
					/>
				</SearchField>
			)}
			<CreatorList>
				{filteredCreators.length === 0 ? (
					<EmptyState>
						{creators.length === 0
							? "No creators in this stage"
							: searchQuery.trim()
								? "No creators match your search"
								: "No creators in this stage"}
					</EmptyState>
				) : (
					filteredCreators.map((creator) => {
						const name = creator.displayName || creator.handle;
						const market = creator.marketName;
						const marketCode = market ? getCountryShort(market) : null;
						const flagUrl = market ? getCountryFlagUrl(market) : "";

						return (
							<CreatorRow
								key={creator.assignmentId}
								type="button"
								onClick={() => onCreatorClick(creator)}
							>
								<CreatorAvatar src={creator.avatarUrl} alt="" />
								<CreatorName title={name}>{name}</CreatorName>
								{marketCode && (
									<MarketChip title={market || undefined}>
										{flagUrl ? (
											<MarketFlag src={flagUrl} alt="" />
										) : (
											<MarketFlagFallback>{getCountryFlag(market || "")}</MarketFlagFallback>
										)}
										<MarketCode>{marketCode}</MarketCode>
									</MarketChip>
								)}
							</CreatorRow>
						);
					})
				)}
			</CreatorList>
		</PopoverBody>
	);

	return (
		<>
			<PopoverGlobalStyle />
			<Popover
				content={content}
				trigger="click"
				open={open}
				onOpenChange={onOpenChange}
				placement="bottomRight"
				overlayClassName="campaign-status-creators-popover"
				arrow={false}
			>
				{children}
			</Popover>
		</>
	);
};
